import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { generateMatchTeams } from "../sport/matchTeams";
import Avatar from "./Avatar";

const READY_SECONDS = 12;

export default function RankedModal({ open, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rating, setRating] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | searching | found
  const [elapsed, setElapsed] = useState(0);
  const [foundMatch, setFoundMatch] = useState(null);
  const [readyLeft, setReadyLeft] = useState(READY_SECONDS);
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  // Load my ranked rating when the modal opens
  useEffect(() => {
    if (!open || !user) return;
    supabase
      .from("leaderboard_entries")
      .select("glicko_rating")
      .eq("user_id", user.id)
      .eq("mode", "ranked")
      .maybeSingle()
      .then(({ data }) => setRating(data?.glicko_rating ?? 1500));
  }, [open, user]);

  const cleanup = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    pollRef.current = null;
    timerRef.current = null;
  };

  // Leave queue + reset when the modal closes
  useEffect(() => {
    if (!open) {
      cleanup();
      setStatus("idle");
      setFoundMatch(null);
      setElapsed(0);
      if (user)
        supabase.from("matchmaking_queue").delete().eq("user_id", user.id);
    }
    return () => cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Ready countdown, synced to the match's created_at so both players align
  useEffect(() => {
    if (status !== "found" || !foundMatch) return;
    const target =
      new Date(foundMatch.createdAt).getTime() + READY_SECONDS * 1000;
    const tick = () => {
      const left = Math.max(0, Math.ceil((target - Date.now()) / 1000));
      setReadyLeft(left);
      if (left <= 0) {
        cleanup();
        navigate(`/ranked/${foundMatch.id}`);
      }
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [status, foundMatch, navigate]);

  if (!open) return null;

  const enterFound = async (matchId) => {
    cleanup();
    const { data: m } = await supabase
      .from("matches")
      .select("*")
      .eq("id", matchId)
      .maybeSingle();
    if (!m) return;
    const ids = [m.player1, m.player2];
    const { data: profs } = await supabase
      .from("profiles")
      .select("id, username, avatar_url")
      .in("id", ids);
    const { data: entries } = await supabase
      .from("leaderboard_entries")
      .select("user_id, glicko_rating")
      .eq("mode", "ranked")
      .in("user_id", ids);
    const players = ids.map((id) => ({
      id,
      username: profs?.find((p) => p.id === id)?.username || "player",
      avatarUrl: profs?.find((p) => p.id === id)?.avatar_url ?? null,
      rating: entries?.find((e) => e.user_id === id)?.glicko_rating ?? 1500,
    }));
    setFoundMatch({ id: matchId, createdAt: m.created_at, players });
    setReadyLeft(READY_SECONDS);
    setStatus("found");
  };

  const poll = async () => {
    await supabase
      .from("matchmaking_queue")
      .update({ joined_at: new Date().toISOString() })
      .eq("user_id", user.id);

    const { data: matchId } = await supabase.rpc("try_matchmake", {
      p_teams: generateMatchTeams(),
    });
    if (matchId) return enterFound(matchId);

    const { data: existing } = await supabase
      .from("matches")
      .select("id")
      .or(`player1.eq.${user.id},player2.eq.${user.id}`)
      .eq("status", "active")
      .gt("created_at", new Date(Date.now() - 120000).toISOString())
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (existing) enterFound(existing.id);
  };

  const startSearch = async () => {
    if (!user) return;
    setStatus("searching");
    setElapsed(0);
    await supabase.from("matchmaking_queue").upsert({
      user_id: user.id,
      glicko_rating: rating ?? 1500,
      joined_at: new Date().toISOString(),
    });
    timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    pollRef.current = setInterval(poll, 1500);
    poll();
  };

  const cancelSearch = async () => {
    cleanup();
    setStatus("idle");
    await supabase.from("matchmaking_queue").delete().eq("user_id", user.id);
  };

  // --- Match found screen ---
  if (status === "found" && foundMatch) {
    const me = foundMatch.players.find((p) => p.id === user.id);
    const opp = foundMatch.players.find((p) => p.id !== user.id);

    const PlayerTile = ({ p, highlight }) => (
      <div
        className={`flex-1 rounded-2xl border p-5 flex flex-col items-center gap-3
          ${highlight ? "bg-emerald-500/5 border-emerald-500/40" : "bg-zinc-900/40 border-zinc-800"}`}
      >
        <Avatar username={p?.username} url={p?.avatarUrl} size={56} />
        <div className="text-center min-w-0">
          <div className="text-sm font-semibold text-zinc-100 truncate max-w-[130px]">
            {p?.username}
          </div>
          <div className="text-xs text-emerald-400 font-bold tabular-nums mt-0.5">
            {Math.round(p?.rating ?? 1500)}{" "}
            <span className="text-zinc-600">SR</span>
          </div>
        </div>
      </div>
    );

    return (
      <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm grid place-items-center p-6">
        <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-md p-8 animate-scale-in shadow-2xl shadow-black/50 text-center">
          <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-500/80 mb-6">
            Match found
          </div>

          <div className="flex items-stretch gap-3">
            <PlayerTile p={me} highlight />
            <div className="grid place-items-center text-zinc-600 text-sm font-semibold">
              vs
            </div>
            <PlayerTile p={opp} />
          </div>

          <div className="mt-8">
            <div className="text-5xl font-black text-emerald-400 tabular-nums leading-none">
              {readyLeft}
            </div>
            <div className="text-sm text-zinc-400 mt-3">Get ready…</div>
          </div>
        </div>
      </div>
    );
  }

  // --- Idle / searching ---
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm grid place-items-center p-6"
      onClick={status === "searching" ? undefined : onClose}
    >
      <div
        className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-sm
                   p-8 animate-scale-in shadow-2xl shadow-black/50 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
          Ranked
        </div>
        <div className="text-4xl font-bold text-emerald-400 tabular-nums">
          {rating != null ? Math.round(rating) : "—"}
          <span className="text-sm text-zinc-500 ml-2">SR</span>
        </div>

        {!user ? (
          <p className="mt-6 text-sm text-zinc-400">Sign in to play ranked.</p>
        ) : status === "searching" ? (
          <div className="mt-8">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-sm text-zinc-300">
                Searching… {elapsed}s
              </span>
            </div>
            <button
              onClick={cancelSearch}
              className="w-full py-2.5 rounded-full bg-zinc-900 border border-zinc-800
                         text-sm font-semibold text-zinc-300 hover:border-zinc-700 transition"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="mt-8 space-y-3">
            <button
              onClick={startSearch}
              className="w-full py-3 rounded-full bg-emerald-500 text-zinc-950
                         font-semibold text-sm hover:bg-emerald-400 transition"
            >
              Find match
            </button>
            <button
              onClick={onClose}
              className="w-full py-2.5 rounded-full text-sm text-zinc-500 hover:text-zinc-300 transition"
            >
              Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
